import { defineStore } from "pinia"
import { useGridCollectionStore } from "@/stores/grid-collection"
import { TableState } from "@/shared/models/table"
import { ToastService } from "@/shared/services/toast"
import { MarketingCampaignService } from "@/shared/services/marketing-campaign"
import { MarketingCampaign } from "@/shared/models/marketing-campaign"
import { GgmsError } from "@/shared/services/client"

export const useMarketingCampaignStore = defineStore("marketing-campaign", {
    state: () => ({
        tableState: {
            data: [],
            selectedData: [],
            currentPage: 0,
            pageLength: 50,
            numberOfPages: 0,
            total: 0,
            isAllSelected: false,
            isLoading: false,
        } as TableState<MarketingCampaign>,
        marketingCampaignService: new MarketingCampaignService(),
        toastService: new ToastService(),
        gridCollectionStore: useGridCollectionStore(),
    }),
    actions: {
        async getMarketingCampaigns() {
            try {
                this.tableState.isLoading = true

                const filtersGroups = this.gridCollectionStore.grid.filtersGroups.length
                    ? encodeURIComponent(JSON.stringify(this.gridCollectionStore.grid.filtersGroups))
                    : undefined

                const response = await this.marketingCampaignService.getAll({
                    length: this.tableState.pageLength,
                    column: this.tableState.column,
                    order: this.tableState.order,
                    search: this.tableState.search,
                    filtersGroups,
                })

                if (!response?.data?.length) {
                    this.resetTableState()
                    return
                }

                this.tableState.selectedData = []
                this.tableState.data = response.data
                this.tableState.total = response.total
                this.tableState.numberOfPages = Math.ceil(this.tableState.total / this.tableState.pageLength)
                this.tableState.lastPage = this.tableState.currentPage
            } catch (error) {
                this.toastService.addToast({
                    type: "error",
                    message: "Failed to load Marketing Campaigns.",
                })
            } finally {
                this.tableState.isLoading = false
            }
        },

        resetTableState() {
            this.tableState.data = []
            this.tableState.total = 0
            this.tableState.selectedData = []
            this.tableState.numberOfPages = 1
        },

        async getMarketingCampaignsGrid() {
            try {
                this.tableState.isLoading = true
                const response = await this.gridCollectionStore.getGrid("marketing-campaign")
                await this.getMarketingCampaigns()

                return response
            } finally {
                this.tableState.isLoading = false
            }
        },

        async updateMarketingCampaignsGrid() {
            try {
                this.tableState.isLoading = true
                if (!this.gridCollectionStore.grid) return
                this.gridCollectionStore.grid.filtersGroups = this.gridCollectionStore.filtersGroups || []

                const response = await this.gridCollectionStore.updateGrid(
                    "marketing-campaign",
                    this.gridCollectionStore.grid
                )
                this.gridCollectionStore.gridResponse = response
                this.gridCollectionStore.grid = this.gridCollectionStore.gridResponse.grid

                await this.getMarketingCampaigns()
            } finally {
                this.tableState.isLoading = false
            }
        },

        async checkMarketingCampaignList(typeName: string, campaignIds: string[]) {
            try {
                return this.marketingCampaignService.checkMarketingCampaignList(typeName, campaignIds)
            } catch (error) {
                const err = error as GgmsError
                if (err.code === "ValidationError") {
                    this.toastService.addToast({
                        type: "error",
                        message: err.message,
                    })
                }
            }
        },
    },
})
